import React, { Fragment, useEffect, useState } from "react";
import Dashboard from "../Admin/Dashboard/Dashboard";
import CurrentDate from "../Admin/CurrentDate";
import Loader from "../Admin/Dashboard/Loader";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import baseApi from "../api";
import { useFormik } from "formik";
import * as Yup from "yup";
import "../../index.css";

const REQUIRED_SCHEMA_TYPES = ["FAQPage"];
const SCHEMA_KEY_MAP = {
  FAQPage: "faqPage"
};

const DEFAULT_SCHEMA_TEMPLATES = {
  FAQPage: {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "",
        acceptedAnswer: {
          "@type": "Answer",
          text: ""
        }
      }
    ]
  },
  Custom: {
    "@context": "https://schema.org",
    "@type": ""
  }
};

const getDefaultSchemaByType = (type) =>
  JSON.stringify(DEFAULT_SCHEMA_TEMPLATES[type] || DEFAULT_SCHEMA_TEMPLATES.Custom, null, 2);

const parseSchemaValue = (schemaType, schemaText) => {
  if (!schemaText || !schemaText.trim()) {
    return null;
  }
  return JSON.parse(schemaText);
};

const getSchemaItemsForEdit = (record, id) => {
  const requiredItems = REQUIRED_SCHEMA_TYPES.map((type) => ({
    type,
    schema: getDefaultSchemaByType(type),
    isRequired: true,
    sourceId: null
  }));

  if (!record) {
    return requiredItems;
  }

  if (record.schema && typeof record.schema === "object" && !Array.isArray(record.schema)) {
    const updatedRequiredItems = requiredItems.map((item) => {
      const schemaKey = SCHEMA_KEY_MAP[item.type];
      const schemaValue = record.schema[schemaKey];
      return schemaValue
        ? { ...item, schema: JSON.stringify(schemaValue, null, 2), sourceId: id }
        : item;
    });

    const customItems = Array.isArray(record.customSchema)
      ? record.customSchema.map((customItem) => ({
          type: "Custom",
          schema: JSON.stringify(customItem || DEFAULT_SCHEMA_TEMPLATES.Custom, null, 2),
          isRequired: false,
          sourceId: null
        }))
      : [];

    return [...updatedRequiredItems, ...customItems];
  }

  // fallback for legacy single schema record shape
  if (!record.type) {
    return requiredItems;
  }

  const requiredIndex = requiredItems.findIndex((item) => item.type === record.type);
  const recordSchema = JSON.stringify(record.schema || DEFAULT_SCHEMA_TEMPLATES.Custom, null, 2);

  if (requiredIndex >= 0) {
    requiredItems[requiredIndex] = {
      ...requiredItems[requiredIndex],
      schema: recordSchema,
      sourceId: id
    };
    return requiredItems;
  }

  return [
    ...requiredItems,
    {
      type: "Custom",
      schema: recordSchema,
      isRequired: false,
      sourceId: id
    }
  ];
};

const buildCombinedPayload = (values) => {
  const schema = {};

  REQUIRED_SCHEMA_TYPES.forEach((schemaType) => {
    const currentItem = values.schemaItems.find((item) => item.type === schemaType);
    schema[SCHEMA_KEY_MAP[schemaType]] = currentItem
      ? parseSchemaValue(schemaType, currentItem.schema)
      : null;
  });

  const customSchema = values.schemaItems
    .filter((item) => item.type === "Custom")
    .map((item) => parseSchemaValue(item.type, item.schema))
    .filter(Boolean);

  return {
    page: values.page,
    schema,
    customSchema
  };
};

const EditWebsiteSchema = () => {
  const [isLoading, setIsloading] = useState(false);
  const [schemaData, setSchemaData] = useState({});
  const { id } = useParams();

  useEffect(() => {
    setIsloading(true);
    axios.get(`${baseApi}website-schema/${id}`).then((res) => {
      const record = res.data.data || {};
      setSchemaData({
        ...record,
        schemaItems: getSchemaItemsForEdit(record, id)
      });
      setIsloading(false);
    });
  }, [id]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      page: schemaData.page || "global",
      isActive: true,
      schemaItems: schemaData.schemaItems || getSchemaItemsForEdit(null, id)
    },
    validationSchema: Yup.object({
      page: Yup.string(),
      schemaItems: Yup.array().of(
        Yup.object({
          type: Yup.string(),
          schema: Yup.string().test("is-json", "Schema must be valid JSON", (value) => {
            if (!value || !value.trim()) return true;
            try {
              JSON.parse(value);
              return true;
            } catch (error) {
              return false;
            }
          })
        })
      )
    }),
    onSubmit: async (values) => {
      setIsloading(true);
      try {
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("jwtToken")}`
        };
        const payload = buildCombinedPayload(values);

        await axios.put(`${baseApi}website-schema/${id}`, payload, {
          headers
        });
      } catch (err) {
        console.log(err);
      } finally {
        setIsloading(false);
      }
    }
  });

  const addSchemaRow = () => {
    const nextItems = [
      ...formik.values.schemaItems,
      { type: "Custom", schema: getDefaultSchemaByType("Custom"), isRequired: false, sourceId: null }
    ];
    formik.setFieldValue("schemaItems", nextItems);
  };

  const removeSchemaRow = (index) => {
    if (formik.values.schemaItems.length === 1) return;
    const nextItems = formik.values.schemaItems.filter((_, idx) => idx !== index);
    formik.setFieldValue("schemaItems", nextItems);
  };

  return (
    <Fragment>
      <div className="admin_panel">
        <Dashboard />
        {!isLoading ? (
          <div className="container">
            <div className="col-sm-12">
              <CurrentDate />
              <form onSubmit={formik.handleSubmit} encType="application/json">
                <div className="form-group">
                  <label>
                    <h3>Page/Slug</h3>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="page"
                    name="page"
                    {...formik.getFieldProps("page")}
                  />
                  <span className="text-danger">
                    {formik.touched.page && formik.errors.page ? (
                      <div>{formik.errors.page}</div>
                    ) : null}
                  </span>
                </div>

                {formik.values.schemaItems.map((item, index) => (
                  <div className="form-group" key={`schema-item-${index}`}>
                    <label>
                      <h3>{`Schema ${index + 1} Type`}</h3>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name={`schemaItems.${index}.type`}
                      value={item.type}
                      readOnly
                    />

                    <label style={{ marginTop: "12px" }}>
                      <h3>{`Schema ${index + 1} JSON`}</h3>
                    </label>
                    <textarea
                      className="form-control"
                      name={`schemaItems.${index}.schema`}
                      rows="10"
                      placeholder="Enter schema JSON here..."
                      value={item.schema}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <span className="text-danger">
                      {formik.touched.schemaItems &&
                      formik.touched.schemaItems[index] &&
                      formik.touched.schemaItems[index].schema &&
                      formik.errors.schemaItems &&
                      formik.errors.schemaItems[index] &&
                      formik.errors.schemaItems[index].schema ? (
                        <div>{formik.errors.schemaItems[index].schema}</div>
                      ) : null}
                    </span>

                    {!item.isRequired && !item.sourceId ? (
                      <div style={{ marginTop: "10px" }}>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeSchemaRow(index)}
                        >
                          Remove Schema
                        </button>
                      </div>
                    ) : null}
                    <hr />
                  </div>
                ))}

                <div className="form-group">
                  <button type="button" className="btn btn-default" onClick={addSchemaRow}>
                    Add More Schema Type
                  </button>
                  &nbsp;&nbsp;
                  <input type="submit" className="btn btn-primary" value="Update Schema" />
                  &nbsp;&nbsp;
                  <Link to="/website-schema">Back to website schema</Link>
                </div>
              </form>
            </div>
          </div>
        ) : (
          <Loader />
        )}
      </div>
    </Fragment>
  );
};

export default EditWebsiteSchema;
